<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div class="block-config-editor">
    <div class="editor-header">
      <div>
        <h3 class="editor-title">
          {{ $t("dashboardAdmin.blockEditor.title") }}
        </h3>
        <span class="editor-block-id">{{ editableBlock.id }}</span>
      </div>
      <PrimeButton
        icon="ri-close-line"
        text
        rounded
        severity="secondary"
        @click="$emit('close')"
      />
    </div>

    <div class="editor-form">
      <!-- Block Type -->
      <div class="field">
        <FieldLabel
          :label="$t('dashboardAdmin.blockEditor.type')"
          :help="$t('dashboardAdmin.blockEditor.help.type')"
        />
        <PrimeSelect
          v-model="editableBlock.type"
          :options="blockTypes"
          option-label="label"
          option-value="value"
          class="select-full"
          @change="onTypeChange"
        />
      </div>

      <!-- Block Title -->
      <div class="field">
        <FieldLabel
          :label="$t('dashboardAdmin.blockEditor.titlePlaceholder')"
          :help="$t('dashboardAdmin.blockEditor.help.title')"
        />
        <PrimeInputText
          v-model="editableBlock.title"
          class="input-full"
          :placeholder="$t('dashboardAdmin.blockEditor.titlePlaceholder')"
        />
      </div>

      <!-- Block-specific Configuration -->
      <PrimeFieldset :legend="$t('dashboardAdmin.blockEditor.config')">
        <component
          :is="currentConfigComponent"
          :config="editableBlock.config"
          :chartDataJson="chartDataJson"
          :bookmarksJson="bookmarksJson"
          :linksJson="linksJson"
          @update:config="updateConfig"
          @update:links-json="linksJson = $event"
          @open-markdown-editor="showMarkdownEditor = true"
          @open-markdown-browser="openMarkdownBrowser"
          @open-file-browser="openFileBrowser"
          @open-json-editor="openJsonEditor"
        />
      </PrimeFieldset>

      <!-- Route Configuration (optional) -->
      <PrimeFieldset
        :legend="$t('dashboardAdmin.blockEditor.route')"
        :toggleable="true"
        :collapsed="!editableBlock.route"
      >
        <div class="field">
          <FieldLabel
            :label="$t('dashboardAdmin.blockEditor.routePage')"
            :help="$t('dashboardAdmin.blockEditor.help.routePage')"
          />
          <PrimeSelect
            v-model="routeName"
            :options="routeOptions"
            option-label="label"
            option-value="value"
            class="select-full"
            :placeholder="$t('dashboardAdmin.blockEditor.routePagePlaceholder')"
            showClear
          />
        </div>
      </PrimeFieldset>

      <!-- Grid Position (only for blocks on grid) -->
      <PrimeFieldset
        v-if="editableBlock.gridPosition"
        :legend="$t('dashboardAdmin.blockEditor.position')"
      >
        <div class="position-fields">
          <div class="position-row">
            <div class="position-field">
              <FieldLabel
                :label="$t('dashboardAdmin.blockEditor.colStart')"
                :help="$t('dashboardAdmin.blockEditor.help.colStart')"
              />
              <PrimeInputNumber
                v-model="editableBlock.gridPosition.colStart"
                :min="1"
                class="input-full"
              />
            </div>
            <div class="position-field">
              <FieldLabel
                :label="$t('dashboardAdmin.blockEditor.colEnd')"
                :help="$t('dashboardAdmin.blockEditor.help.colEnd')"
              />
              <PrimeInputNumber
                v-model="editableBlock.gridPosition.colEnd"
                :min="editableBlock.gridPosition.colStart + 1"
                class="input-full"
              />
            </div>
          </div>
          <div class="position-row">
            <div class="position-field">
              <FieldLabel
                :label="$t('dashboardAdmin.blockEditor.rowStart')"
                :help="$t('dashboardAdmin.blockEditor.help.rowStart')"
              />
              <PrimeInputNumber
                v-model="editableBlock.gridPosition.rowStart"
                :min="1"
                class="input-full"
              />
            </div>
            <div class="position-field">
              <FieldLabel
                :label="$t('dashboardAdmin.blockEditor.rowEnd')"
                :help="$t('dashboardAdmin.blockEditor.help.rowEnd')"
              />
              <PrimeInputNumber
                v-model="editableBlock.gridPosition.rowEnd"
                :min="editableBlock.gridPosition.rowStart + 1"
                class="input-full"
              />
            </div>
          </div>
        </div>
      </PrimeFieldset>

      <!-- Message for basket blocks -->
      <div v-else class="basket-info">
        <p class="basket-info-text">
          <i class="ri-information-line basket-info-icon"></i>
          {{ $t("dashboardAdmin.blockEditor.basketBlockInfo") }}
        </p>
      </div>

      <div class="apply-row">
        <PrimeButton
          :label="$t('dashboardAdmin.blockEditor.apply')"
          class="apply-btn"
          @click="applyChanges"
        />
      </div>
    </div>

    <!-- Markdown Editor Dialog -->
    <MarkdownEditorDialog
      v-model="showMarkdownEditor"
      :content="editableBlock.config?.markdown || ''"
      @save="handleMarkdownSave"
    />

    <!-- JSON Editor Dialog -->
    <JsonEditorDialog
      v-model="showJsonEditor"
      :content="currentJsonContent"
      @save="handleJsonSave"
    />

    <!-- Markdown File Browser Dialog -->
    <PrimeDialog
      v-model:visible="showMarkdownBrowser"
      modal
      :header="$t('dashboardAdmin.blockEditor.selectMarkdownFile')"
      :style="{ width: '80rem', height: '80vh' }"
    >
      <div class="browser-layout" style="height: calc(80vh - 180px)">
        <div class="tree-panel">
          <TreeExplorer
            :showUserHome="true"
            :showFiles="true"
            @selectNode="onMarkdownNodeSelected"
          />
        </div>
        <div class="preview-panel">
          <div v-if="!selectedMarkdownFile" class="preview-placeholder">
            <div class="preview-placeholder-content">
              <i class="ri-file-text-line preview-placeholder-icon"></i>
              <p>{{ $t("dashboardAdmin.blockEditor.selectFileToPreview") }}</p>
            </div>
          </div>
          <div v-else-if="markdownPreviewLoading" class="preview-placeholder">
            <PrimeProgressSpinner />
          </div>
          <iframe
            v-else-if="markdownPreviewHtml"
            :srcdoc="markdownPreviewHtml"
            class="preview-iframe"
            sandbox="allow-scripts allow-popups"
          ></iframe>
          <div v-else-if="markdownPreviewError" class="preview-placeholder">
            <div class="preview-placeholder-content">
              <i
                class="ri-error-warning-line preview-placeholder-icon preview-error-icon"
              ></i>
              <p class="preview-error-text">{{ markdownPreviewError }}</p>
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="browser-footer">
          <div class="browser-footer-info">
            <div v-if="selectedMarkdownFile" class="selected-file-info">
              <span class="selected-file-label">
                {{ $t("dashboardAdmin.blockEditor.selected") }}:
              </span>
              <span
                v-tooltip.top="selectedMarkdownFile?.name"
                class="selected-file-name"
              >
                {{ selectedMarkdownFile?.name }}
              </span>
            </div>
            <div v-if="markdownSelectionError" class="p-error">
              {{ markdownSelectionError }}
            </div>
          </div>
          <div class="browser-footer-actions">
            <PrimeButton
              :label="$t('cancel')"
              severity="secondary"
              text
              @click="showMarkdownBrowser = false"
            />
            <PrimeButton
              :disabled="!selectedMarkdownFile"
              :label="$t('dashboardAdmin.blockEditor.select')"
              icon="ri-check-line"
              @click="confirmMarkdownSelection"
            />
          </div>
        </div>
      </template>
    </PrimeDialog>

    <!-- File Browser Dialog (PDF/Image) -->
    <PrimeDialog
      v-model:visible="showFileBrowser"
      modal
      :header="$t('dashboardAdmin.blockEditor.selectFile')"
      :style="{ width: '50rem', height: '70vh' }"
    >
      <div style="height: calc(70vh - 180px); overflow: auto">
        <TreeExplorer
          :showUserHome="true"
          :showFiles="true"
          @selectNode="onFileNodeSelected"
        />
      </div>
      <template #footer>
        <div class="browser-footer">
          <div class="browser-footer-info">
            <div v-if="selectedFile" class="selected-file-info">
              <span class="selected-file-label">
                {{ $t("dashboardAdmin.blockEditor.selected") }}:
              </span>
              <span
                v-tooltip.top="selectedFile?.name"
                class="selected-file-name"
              >
                {{ selectedFile?.name }}
              </span>
            </div>
            <div v-if="fileSelectionError" class="p-error">
              {{ fileSelectionError }}
            </div>
          </div>
          <div class="browser-footer-actions">
            <PrimeButton
              :label="$t('cancel')"
              severity="secondary"
              text
              @click="showFileBrowser = false"
            />
            <PrimeButton
              :disabled="!selectedFile"
              :label="$t('dashboardAdmin.blockEditor.select')"
              icon="ri-check-line"
              @click="confirmFileSelection"
            />
          </div>
        </div>
      </template>
    </PrimeDialog>
  </div>
</template>

<script>
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import Select from "primevue/select";
import Fieldset from "primevue/fieldset";
import Dialog from "primevue/dialog";
import ProgressSpinner from "primevue/progressspinner";
import Tooltip from "primevue/tooltip";
import MarkdownEditorDialog from "@/components/dashboard/editor/MarkdownEditorDialog.vue";
import JsonEditorDialog from "@/components/dashboard/editor/JsonEditorDialog.vue";
import FieldLabel from "@/components/dashboard/editor/FieldLabel.vue";
import TreeExplorer from "@/components/TreeExplorer.vue";
import alfrescoNodeService from "@/services/AlfrescoNodeService.js";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { validators } from "@/components/dashboard/editor/validators";

// Import block config components
import MarkdownConfig from "@/components/dashboard/editor/block-configs/MarkdownConfig.vue";
import ChartConfig from "@/components/dashboard/editor/block-configs/ChartConfig.vue";
import FavoritesConfig from "@/components/dashboard/editor/block-configs/FavoritesConfig.vue";
import BookmarksConfig from "@/components/dashboard/editor/block-configs/BookmarksConfig.vue";
import ExternalAppsConfig from "@/components/dashboard/editor/block-configs/ExternalAppsConfig.vue";
import PdfImageConfig from "@/components/dashboard/editor/block-configs/PdfImageConfig.vue";
import NoConfig from "@/components/dashboard/editor/block-configs/NoConfig.vue";

export default {
  name: "BlockConfigEditor",
  components: {
    PrimeButton: Button,
    PrimeInputText: InputText,
    PrimeInputNumber: InputNumber,
    PrimeSelect: Select,
    PrimeFieldset: Fieldset,
    PrimeDialog: Dialog,
    PrimeProgressSpinner: ProgressSpinner,
    MarkdownEditorDialog,
    JsonEditorDialog,
    FieldLabel,
    TreeExplorer,
    MarkdownConfig,
    ChartConfig,
    FavoritesConfig,
    BookmarksConfig,
    ExternalAppsConfig,
    PdfImageConfig,
    NoConfig,
  },
  directives: {
    tooltip: Tooltip,
  },
  props: {
    block: {
      type: Object,
      required: true,
    },
    customBlockTypes: {
      type: Array,
      default: () => [],
    },
    customRouteOptions: {
      type: Array,
      default: null,
    },
  },
  emits: ["update:block", "close"],
  data() {
    const blockCopy = JSON.parse(JSON.stringify(this.block));
    // Ensure config is always defined
    if (!blockCopy.config) {
      blockCopy.config = {};
    }
    return {
      editableBlock: blockCopy,
      routeName: this.block.route?.name || "",
      chartDataJson: "",
      bookmarksJson: "",
      linksJson: "",
      showMarkdownEditor: false,
      showJsonEditor: false,
      currentJsonType: null,
      showMarkdownBrowser: false,
      selectedMarkdownFile: null,
      markdownSelectionError: "",
      markdownPreviewHtml: "",
      markdownPreviewLoading: false,
      markdownPreviewError: "",
      showFileBrowser: false,
      selectedFile: null,
      fileSelectionError: "",
    };
  },
  computed: {
    currentConfigComponent() {
      // Check custom block types first
      const customType = this.customBlockTypes.find(
        (ct) => ct.value === this.editableBlock.type,
      );
      if (customType?.configComponent) {
        return customType.configComponent;
      }

      const componentMap = {
        markdown: "MarkdownConfig",
        chart: "ChartConfig",
        pdf: "PdfImageConfig",
        image: "PdfImageConfig",
        favorites: "FavoritesConfig",
        bookmarks: "BookmarksConfig",
        externalApps: "ExternalAppsConfig",
      };

      return componentMap[this.editableBlock.type] || "NoConfig";
    },
    blockTypes() {
      const builtIn = [
        {
          label: this.$t("dashboardAdmin.blockTypes.markdown"),
          value: "markdown",
        },
        {
          label: this.$t("dashboardAdmin.blockTypes.favorites"),
          value: "favorites",
        },
        { label: this.$t("dashboardAdmin.blockTypes.chart"), value: "chart" },
        {
          label: this.$t("dashboardAdmin.blockTypes.myActivity"),
          value: "myActivity",
        },
        { label: this.$t("dashboardAdmin.blockTypes.pdf"), value: "pdf" },
        { label: this.$t("dashboardAdmin.blockTypes.image"), value: "image" },
        {
          label: this.$t("dashboardAdmin.blockTypes.bookmarks"),
          value: "bookmarks",
        },
        { label: this.$t("dashboardAdmin.blockTypes.search"), value: "search" },
        {
          label: this.$t("dashboardAdmin.blockTypes.externalApps"),
          value: "externalApps",
        },
      ];

      // Append custom block types
      const custom = this.customBlockTypes.map((ct) => ({
        label: ct.label,
        value: ct.value,
      }));

      return [...builtIn, ...custom];
    },
    routeOptions() {
      if (this.customRouteOptions) {
        return this.customRouteOptions;
      }
      return [
        {
          label: this.$t("dashboardAdmin.blockEditor.routePages.tasks"),
          value: "tasks",
        },
        {
          label: this.$t("dashboardAdmin.blockEditor.routePages.requests"),
          value: "requests",
        },
      ];
    },
    currentJsonContent() {
      if (this.currentJsonType === "chart") {
        return this.chartDataJson;
      } else if (this.currentJsonType === "bookmarks") {
        return this.bookmarksJson;
      } else if (this.currentJsonType === "externalApps") {
        return this.linksJson;
      }
      return "";
    },
  },
  watch: {
    block: {
      deep: true,
      handler(newBlock, oldBlock) {
        // If editing a different block (ID changed), load everything
        if (!oldBlock || newBlock.id !== oldBlock.id) {
          const blockCopy = JSON.parse(JSON.stringify(newBlock));
          // Ensure config is always defined
          if (!blockCopy.config) {
            blockCopy.config = {};
          }
          this.editableBlock = blockCopy;
          this.routeName = newBlock.route?.name || "";
          this.initializeJsonFields();
        } else if (newBlock.gridPosition) {
          // Same block, sync grid position if it changed
          // This handles: block placed from basket, resized, or moved on grid
          const posChanged =
            !oldBlock.gridPosition ||
            !this.editableBlock.gridPosition ||
            newBlock.gridPosition.colStart !==
              this.editableBlock.gridPosition.colStart ||
            newBlock.gridPosition.colEnd !==
              this.editableBlock.gridPosition.colEnd ||
            newBlock.gridPosition.rowStart !==
              this.editableBlock.gridPosition.rowStart ||
            newBlock.gridPosition.rowEnd !==
              this.editableBlock.gridPosition.rowEnd;

          if (posChanged) {
            // Sync grid position from parent block
            this.editableBlock.gridPosition = {
              colStart: newBlock.gridPosition.colStart,
              colEnd: newBlock.gridPosition.colEnd,
              rowStart: newBlock.gridPosition.rowStart,
              rowEnd: newBlock.gridPosition.rowEnd,
            };
          }
        }
      },
    },
    linksJson(newValue) {
      // Update internal config when linksJson changes
      // The actual emission happens when clicking "Apply"
      if (this.editableBlock.type === "externalApps") {
        try {
          const parsedLinks = JSON.parse(newValue);
          this.editableBlock.config.links = parsedLinks;
        } catch (e) {
          console.error("[BlockConfigEditor] Failed to parse linksJson:", e);
          this.$toast.add({
            severity: "error",
            summary: this.$t("dashboardAdmin.blockEditor.jsonParseError"),
            detail: e.message,
            life: 4000,
          });
        }
      }
    },
  },
  mounted() {
    this.initializeJsonFields();
  },
  methods: {
    updateConfig(newConfig) {
      this.editableBlock.config = newConfig;
    },
    initializeJsonFields() {
      // Initialize JSON text fields for complex config
      if (
        this.editableBlock.type === "chart" &&
        this.editableBlock.config?.data
      ) {
        this.chartDataJson = JSON.stringify(
          this.editableBlock.config.data,
          null,
          2,
        );
      }
      if (
        this.editableBlock.type === "bookmarks" &&
        this.editableBlock.config?.bookmarks
      ) {
        this.bookmarksJson = JSON.stringify(
          this.editableBlock.config.bookmarks,
          null,
          2,
        );
      }
      if (this.editableBlock.type === "externalApps") {
        this.linksJson = JSON.stringify(
          this.editableBlock.config?.links || [],
          null,
          2,
        );
      }
    },
    onTypeChange() {
      // Reset config when type changes
      this.editableBlock.config = this.getDefaultConfig(
        this.editableBlock.type,
      );
      this.initializeJsonFields();

      this.setDefaultRoute(this.editableBlock.type);
    },
    setDefaultRoute(type) {
      // Check custom block types first
      const customType = this.customBlockTypes.find((ct) => ct.value === type);
      if (customType?.defaultRoute) {
        this.routeName = customType.defaultRoute.name || "";
        return;
      }

      this.routeName = "";
    },
    getDefaultConfig(type) {
      // Check custom block types first
      const customType = this.customBlockTypes.find((ct) => ct.value === type);
      if (customType) {
        return customType.defaultConfig || {};
      }

      const defaults = {
        markdown: { id: "", markdown: "# Content" },
        chart: {
          type: "pie",
          data: { labels: [], datasets: [] },
          options: { responsive: true, maintainAspectRatio: false },
        },
        favorites: { filter: "file" },
        pdf: { id: "" },
        image: { id: "" },
        bookmarks: { bookmarks: [] },
        externalApps: { links: [], useFavicon: false },
      };
      return defaults[type] || {};
    },
    applyChanges() {
      // Parse and validate JSON fields for complex configs
      try {
        if (this.editableBlock.type === "chart") {
          const parsedData = JSON.parse(this.chartDataJson);
          const validation = validators.chartData(parsedData);

          if (!validation.valid) {
            throw new Error(validation.error);
          }

          this.editableBlock.config.data = parsedData;
        }

        if (this.editableBlock.type === "bookmarks") {
          const parsedBookmarks = JSON.parse(this.bookmarksJson);
          const validation = validators.bookmarks(parsedBookmarks);

          if (!validation.valid) {
            throw new Error(validation.error);
          }

          this.editableBlock.config.bookmarks = parsedBookmarks;
        }

        if (this.editableBlock.type === "externalApps") {
          const parsedLinks = JSON.parse(this.linksJson);
          const validation = validators.externalApps(parsedLinks);

          if (!validation.valid) {
            throw new Error(validation.error);
          }

          this.editableBlock.config.links = parsedLinks;
        }
      } catch (error) {
        this.$toast.add({
          severity: "error",
          summary: this.$t("error"),
          detail:
            error.message || this.$t("dashboardAdmin.blockEditor.invalidJson"),
          life: 5000,
        });
        return;
      }

      // Update route
      if (this.routeName) {
        this.editableBlock.route = { name: this.routeName };
      } else {
        delete this.editableBlock.route;
      }

      this.$emit("update:block", this.editableBlock);

      // Show success toast
      this.$toast.add({
        severity: "success",
        summary: this.$t("success"),
        detail: this.$t("dashboardAdmin.blockEditor.changesApplied"),
        life: 3000,
      });

      // Close the editor panel
      this.$emit("close");
    },
    handleMarkdownSave(content) {
      if (!this.editableBlock.config) {
        this.editableBlock.config = {};
      }
      // Reassign the entire config object to trigger reactivity
      this.editableBlock.config = {
        ...this.editableBlock.config,
        markdown: content,
      };
    },
    openJsonEditor(type) {
      this.currentJsonType = type;
      this.showJsonEditor = true;
    },
    handleJsonSave(content) {
      if (this.currentJsonType === "chart") {
        this.chartDataJson = content;
      } else if (this.currentJsonType === "bookmarks") {
        this.bookmarksJson = content;
      } else if (this.currentJsonType === "externalApps") {
        this.linksJson = content;
      }
    },
    openMarkdownBrowser() {
      this.showMarkdownBrowser = true;
      this.selectedMarkdownFile = null;
      this.markdownSelectionError = "";
    },
    async onMarkdownNodeSelected(nodeData) {
      this.markdownSelectionError = "";
      this.markdownPreviewHtml = "";
      this.markdownPreviewError = "";

      // Check if it's a folder
      if (nodeData.isFolder) {
        this.selectedMarkdownFile = null;
        this.markdownSelectionError = this.$t(
          "dashboardAdmin.blockEditor.folderSelectError",
        );
        return;
      }

      // Check if it's a markdown file (.md extension)
      const fileName = nodeData.name || "";
      if (!fileName.toLowerCase().endsWith(".md")) {
        this.selectedMarkdownFile = null;
        this.markdownSelectionError = this.$t(
          "dashboardAdmin.blockEditor.onlyMarkdownFilesAllowed",
        );
        return;
      }

      // Valid markdown file selected
      this.selectedMarkdownFile = nodeData;

      // Set loading state before async operation to avoid UI flicker
      this.markdownPreviewLoading = true;

      // Load and preview the markdown content
      await this.loadMarkdownPreview(nodeData.id);
    },
    async loadMarkdownPreview(nodeId) {
      if (!nodeId) {
        this.markdownPreviewLoading = false;
        return;
      }
      this.markdownPreviewError = "";

      try {
        const blob = await alfrescoNodeService.getNodeContent(nodeId, false);
        const markdownContent = await blob.text();

        // Parse markdown to HTML
        const rawHtml = marked.parse(markdownContent);

        // Add hook to force all links to open in a new tab
        DOMPurify.addHook("afterSanitizeAttributes", function (node) {
          if (node.tagName === "A") {
            node.setAttribute("target", "_blank");
            node.setAttribute("rel", "noopener noreferrer");
          }
        });

        const cleanHtml = DOMPurify.sanitize(rawHtml);

        // Basic CSS for preview
        const styles = `
          body { font-family: sans-serif; color: #333; margin: 1rem; }
          h1, h2, h3 { font-weight: 600; margin-top: 1.5em; margin-bottom: 0.5em; }
          p { margin-bottom: 1em; }
          ul { list-style-type: disc; padding-left: 2em; }
          ol { list-style-type: decimal; padding-left: 2em; }
          blockquote { margin-left: 0; padding-left: 1em; border-left: 3px solid #ccc; color: #666; }
          code { background-color: #eee; padding: 0.2em 0.4em; border-radius: 3px; font-family: monospace; }
          pre { background-color: #eee; padding: 1em; border-radius: 6px; overflow-x: auto; }
          table { border-collapse: collapse; width: 100%; margin-bottom: 1em; }
          th, td { border: 1px solid #ddd; padding: 0.5em; text-align: left; }
          th { background-color: #f5f5f5; font-weight: 600; }
          img { max-width: 100%; height: auto; }
        `;

        this.markdownPreviewHtml = `
          <!DOCTYPE html>
          <html>
            <head>
              <style>${styles}</style>
            </head>
            <body>
              ${cleanHtml}
            </body>
          </html>
        `;
      } catch (error) {
        console.error("Error loading markdown preview:", error);
        this.markdownPreviewError = this.$t(
          "dashboardAdmin.blockEditor.previewLoadError",
        );
      } finally {
        this.markdownPreviewLoading = false;
      }
    },
    confirmMarkdownSelection() {
      if (this.selectedMarkdownFile && this.selectedMarkdownFile.id) {
        if (!this.editableBlock.config) {
          this.editableBlock.config = {};
        }
        // Reassign the entire config object to trigger reactivity
        this.editableBlock.config = {
          ...this.editableBlock.config,
          id: this.selectedMarkdownFile.id,
        };
        this.showMarkdownBrowser = false;
      }
    },
    openFileBrowser() {
      this.showFileBrowser = true;
      this.selectedFile = null;
      this.fileSelectionError = "";
    },
    onFileNodeSelected(nodeData) {
      this.fileSelectionError = "";

      if (nodeData.isFolder) {
        this.selectedFile = null;
        this.fileSelectionError = this.$t(
          "dashboardAdmin.blockEditor.folderSelectError",
        );
        return;
      }

      this.selectedFile = nodeData;
    },
    confirmFileSelection() {
      if (this.selectedFile && this.selectedFile.id) {
        if (!this.editableBlock.config) {
          this.editableBlock.config = {};
        }
        this.editableBlock.config = {
          ...this.editableBlock.config,
          id: this.selectedFile.id,
        };
        this.showFileBrowser = false;
      }
    },
  },
};
</script>

<style scoped>
.block-config-editor {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
  padding: 1rem;
}

.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.editor-title {
  margin: 0;
}

.editor-block-id {
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
}

.editor-form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.field {
  margin-bottom: 1rem;
}

.field label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: var(--p-text-color);
}

.select-full {
  width: 100%;
}

.input-full {
  width: 100%;
}

.position-fields {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.position-row {
  display: flex;
  gap: 0.5rem;
}

.position-field {
  flex: 1;
}

.basket-info {
  padding: 0.75rem;
  background: var(--p-surface-100);
  border-radius: var(--p-border-radius);
}

.basket-info-text {
  margin: 0;
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
}

.basket-info-icon {
  margin-right: 0.5rem;
}

.apply-row {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.apply-btn {
  flex: 1;
}

.font-mono {
  font-family: "Courier New", Courier, monospace;
  font-size: 0.875rem;
}

/* Ensure InputNumber components don't overflow */
:deep(.p-inputnumber) {
  width: 100%;
}

:deep(.p-inputnumber-input) {
  width: 100%;
  max-width: 100%;
}

/* Prevent fieldset from causing overflow */
:deep(.p-fieldset) {
  width: 100%;
  box-sizing: border-box;
}

:deep(.p-fieldset-content) {
  overflow: hidden;
}

/* Browser dialog layouts */
.browser-layout {
  display: flex;
}

.tree-panel {
  width: 50%;
  overflow: auto;
  border-right: 1px solid var(--p-content-border-color);
}

.preview-panel {
  width: 50%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--p-surface-50);
}

.preview-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--p-text-muted-color);
}

.preview-placeholder-content {
  text-align: center;
}

.preview-placeholder-icon {
  font-size: 3.5rem;
  margin-bottom: 0.75rem;
}

.preview-error-icon {
  color: var(--p-red-500);
}

.preview-error-text {
  color: var(--p-red-500);
}

.preview-iframe {
  width: 100%;
  height: 100%;
  border: none;
  background: white;
}

.browser-footer {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  width: 100%;
  justify-content: space-between;
}

.browser-footer-info {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin-right: 0.5rem;
  min-width: 0;
}

.selected-file-info {
  color: var(--p-text-muted-color);
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.selected-file-label {
  color: var(--p-text-muted-color);
  flex-shrink: 0;
}

.selected-file-name {
  text-overflow: ellipsis;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  flex-grow: 1;
}

.browser-footer-actions {
  margin-left: auto;
  display: flex;
  gap: 0.5rem;
}
</style>
